import { fromLocalInput, toLocalInput } from '@/lib/date-input';
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  HOME_LAYOUT_LIMITS,
  checkHomeLayout,
  homeSectionCatalog,
  homeSectionDefinition,
  readSectionItems,
  type HomeLayoutBlock,
  type HomeLayoutProblem,
  type HomeSectionCatalog,
  type HomeSectionVisibility,
} from '@ecom/contracts';
import type { ThemeSectionsState } from '@ecom/sdk';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { settingValue } from '@/stores/settings.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime } from '@/lib/format';
import { categoryNamesBySlug } from '@/lib/options';
import { CONTENT_PAGES } from '@/lib/settings-schema';
import {
  FLAG_COPY,
  GROUP_COPY,
  GROUP_ORDER,
  addBlocker,
  copySection,
  newSection,
  problemMessage,
  renumber,
  itemCopy,
  sectionHint,
  sectionItemCount,
  sectionName,
  sectionSummary,
  withSectionIds,
  type EditorSection,
} from '@/lib/home-sections';
import { moveItem } from '@/lib/description-blocks';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CopyIcon,
  EyeIcon,
  GridIcon,
  GripIcon,
  HeroIcon,
  ImageIcon,
  LayersIcon,
  MailIcon,
  MegaphoneIcon,
  MonitorIcon,
  PaletteIcon,
  PhoneIcon,
  PlusIcon,
  RailIcon,
  ShieldIcon,
  SlidesIcon,
  SparkleIcon,
  TagIcon,
  TextIcon,
  TrashIcon,
} from '@/components/icons';
import { Menu } from '@/components/menu';
import { Page, SaveBar, Segmented, Sheet } from '@/components/page';
import { SortableItem, SortableList } from '@/components/sortable';
import { Alert, Button, Card, EmptyState, IconButton, Input, Pill, Skeleton, Switch, cx } from '@/components/ui';
import { OwnerOnly, useSettings } from '../settings/schema';
import { EditorContext, Group, ItemsEditor, SectionFields, type EditorContextValue } from './fields';

/**
 * The home page, section by section (ADR-056) — the way Shopify, Salla and
 * Zid lay a store's front page out: a list of sections on one side, the chosen
 * one's settings on the other. Sections are added from a picker, dragged or
 * stepped into order, switched off, copied and removed; each is shown on every
 * screen or only some, and can be given dates to run between.
 *
 * Nothing reaches the store until Save. Preview opens the store's own home
 * page drawn from this draft, in its theme, so what is checked is what will be
 * published — not a picture of it.
 */

export function HomeLayoutPage() {
  const t = useT();
  return (
    <Page title={t('homeLayout')} subtitle={t('homeLayoutHint')} width="full">
      <OwnerOnly>
        <HomeLayoutEditor />
      </OwnerOnly>
    </Page>
  );
}

type IconComponent = (props: { className?: string }) => ReactNode;

const TYPE_ICONS: Record<string, IconComponent> = {
  banner: MegaphoneIcon,
  hero: HeroIcon,
  slideshow: SlidesIcon,
  'image-banner': ImageIcon,
  'promo-grid': GridIcon,
  'product-rail': RailIcon,
  'featured-collection': TagIcon,
  categories: LayersIcon,
  features: ShieldIcon,
  'content-block': TextIcon,
  looks: SparkleIcon,
  newsletter: MailIcon,
};

/** A theme's own section wears the palette: the theme drew it. */
const typeIcon = (type: string): IconComponent => TYPE_ICONS[type] ?? PaletteIcon;

const MAX = HOME_LAYOUT_LIMITS.sections;

function HomeLayoutEditor() {
  const { entries, loading, saving, error, save } = useSettings();
  // The store's other lookups wait for the settings, which is when the session is
  // known good: asked while a sign-in is still being restored, they would fail and
  // never ask again.
  const ready = entries.length > 0;
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const layout = (settingValue(entries, 'home.layout') ?? {}) as { blocks?: HomeLayoutBlock[] };
  const stored = useMemo(
    () => withSectionIds([...(layout.blocks ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)),
    [layout.blocks],
  );
  const flags = (settingValue(entries, 'features.flags') ?? {}) as Record<string, boolean>;
  const contentPages = (settingValue(entries, 'pages.content') ?? {}) as Record<string, unknown>;

  const [draft, setDraft] = useState<EditorSection[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Where "Add section" puts the new one: after this position, or at the end.
  const [adding, setAdding] = useState<{ after: number | null } | null>(null);
  // The last section removed, one step back — cheaper than a confirmation on every remove.
  const [removed, setRemoved] = useState<{ section: EditorSection; index: number } | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [categoryNames, setCategoryNames] = useState<Map<string, string> | null>(null);
  // What the deployed theme says it draws (ADR-057); the core's catalog until it answers.
  const [theme, setTheme] = useState<ThemeSectionsState | null>(null);
  const loadTheme = useCallback((refresh = false) => {
    void withSession(() => adminApi.settings.homeSections(refresh))
      .then(setTheme)
      .catch(() => setTheme({ status: 'unreachable', theme: null, problem: null }));
  }, []);
  useEffect(() => {
    if (ready) loadTheme();
  }, [ready, loadTheme]);
  // The API lends the last good description while the storefront is unreachable,
  // and checks saves against it — so the forms are drawn from it too.
  const catalog = useMemo(() => homeSectionCatalog(theme?.theme ?? null), [theme]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    // Named afresh on every visit: a category renamed since should read as renamed.
    const ask = (attempt: number) =>
      void categoryNamesBySlug(true)
        .then((names) => !cancelled && setCategoryNames(names))
        .catch(() => {
          if (!cancelled && attempt < 2) setTimeout(() => ask(attempt + 1), 1500);
        });
    ask(0);
    return () => {
      cancelled = true;
    };
  }, [ready]);

  const sections = draft ?? stored;
  const checked = useMemo(() => checkHomeLayout(sections, catalog), [sections, catalog]);
  const problemsAt = (index: number) => checked.problems.filter((problem) => problem.section === index);
  const troubled = new Set(checked.problems.map((problem) => problem.section)).size;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  const context = useMemo<EditorContextValue>(
    () => ({
      categoryNames,
      catalog,
      pages: [
        ...CONTENT_PAGES.map((page) => ({ slug: page.slug, label: page.label[locale] })),
        ...Object.keys(contentPages)
          .filter((slug) => !CONTENT_PAGES.some((page) => page.slug === slug))
          .map((slug) => ({ slug, label: slug })),
      ],
    }),
    [categoryNames, catalog, contentPages, locale],
  );
  // A section nobody describes is one only the theme can fill in; say why, when that is not by design.
  const undescribed = sections.some((section) => !catalog.has(section.type));
  const themeProblem = theme && theme.status !== 'ok' && undescribed ? theme.status : null;
  // Editable from what was last read, but say that it may be out of date.
  const themeLent = theme?.status === 'unreachable' && theme.theme !== null;

  if (loading && entries.length === 0) {
    return (
      <div className="grid gap-5 xl:grid-cols-[21rem_minmax(0,1fr)]">
        <Skeleton className="h-96" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const commit = (next: EditorSection[]) => {
    setJustSaved(false);
    setDraft(renumber(next));
  };
  const update = (id: string, change: (section: EditorSection) => EditorSection) =>
    commit(sections.map((section) => (section.id === id ? change(section) : section)));

  const add = (type: string) => {
    const section = newSection(type, catalog);
    const at = adding?.after ?? null;
    const next = [...sections];
    next.splice(at === null ? next.length : at + 1, 0, section);
    commit(next);
    setAdding(null);
    setRemoved(null);
    setSelectedId(section.id);
  };
  const remove = (index: number) => {
    const section = sections[index];
    setRemoved({ section, index });
    commit(sections.filter((_, position) => position !== index));
    if (selectedId === section.id) setSelectedId(null);
  };
  const undo = () => {
    if (!removed) return;
    const next = [...sections];
    next.splice(Math.min(removed.index, next.length), 0, removed.section);
    commit(next);
    setSelectedId(removed.section.id);
    setRemoved(null);
  };
  const duplicate = (index: number) => {
    const copy = copySection(sections[index]);
    const next = [...sections];
    next.splice(index + 1, 0, copy);
    commit(next);
    setSelectedId(copy.id);
  };

  const preview = async () => {
    setPreviewError(null);
    setPreviewing(true);
    // Opened inside the click, so no popup blocker stands in the way; pointed
    // at the preview once the API has kept the draft.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const result = await withSession(() => adminApi.settings.homePreview(sections));
      if (!result.url) {
        tab?.close();
        setPreviewError(t('previewNeedsUrl'));
        return;
      }
      // In the admin's language: Arabic at the bare path, English under /en (ADR-021).
      const url = locale === 'en' ? result.url.replace('/preview/home', '/en/preview/home') : result.url;
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (cause) {
      tab?.close();
      setPreviewError(errorMessage(cause, t('previewFailed')));
    } finally {
      setPreviewing(false);
    }
  };

  const selectedIndex = sections.findIndex((section) => section.id === selectedId);
  const selected = selectedIndex >= 0 ? sections[selectedIndex] : null;
  const full = sections.length >= MAX;

  const actionsFor = (index: number) => [
    { label: t('moveUp'), icon: <ArrowLeftIcon className="size-4 rotate-90" />, disabled: index === 0, onSelect: () => commit(moveItem(sections, index, index - 1)) },
    {
      label: t('moveDown'),
      icon: <ArrowLeftIcon className="size-4 -rotate-90" />,
      disabled: index === sections.length - 1,
      onSelect: () => commit(moveItem(sections, index, index + 1)),
    },
    {
      label: t('duplicate'),
      icon: <CopyIcon className="size-4" />,
      // A theme's section copies like any other; a core one only while the page has room for another.
      disabled: addBlocker(sections[index].type, sections, { ...flags, ...alwaysOn(sections[index].type, catalog) }, MAX, catalog) !== null,
      onSelect: () => duplicate(index),
    },
    { label: t('addSectionBelow'), icon: <PlusIcon className="size-4" />, disabled: full, onSelect: () => setAdding({ after: index }) },
    { label: t('remove'), icon: <TrashIcon className="size-4" />, danger: true, onSelect: () => remove(index) },
  ];

  return (
    <EditorContext.Provider value={context}>
      {/* Above both panes, so Preview is in reach whichever of them a narrow screen shows. */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span data-numeric className="text-xs text-ink-muted">
            {t('sectionsCount', { count: count(sections.length, locale), max: count(MAX, locale) })}
          </span>
          <Button variant="secondary" size="sm" disabled={previewing || sections.length === 0} onClick={() => void preview()} title={t('previewHomeHint')}>
            <EyeIcon className="size-4" />
            {t('previewHome')}
          </Button>
        </div>
        {previewError ? <Alert>{previewError}</Alert> : null}
        {themeLent ? (
          <Alert tone="info">
            <span className="block">{t('themeSectionsLent')}</span>
            <Button variant="ghost" size="sm" className="mt-1 -ms-2" onClick={() => loadTheme(true)}>
              {t('tryAgain')}
            </Button>
          </Alert>
        ) : themeProblem ? (
          <Alert tone="info">
            <span className="block">
              {t('themeSectionsUnavailable', { reason: t(`themeSections_${themeProblem}` as const) })}
            </span>
            {themeProblem !== 'none' ? (
              <Button variant="ghost" size="sm" className="mt-1 -ms-2" onClick={() => loadTheme(true)}>
                {t('tryAgain')}
              </Button>
            ) : null}
          </Alert>
        ) : null}
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[21rem_minmax(0,1fr)]">
        {/* The sections. Below a wide screen, the list and the chosen section take turns. */}
        <div className={cx('flex flex-col gap-3 xl:sticky xl:top-5', selected && 'max-xl:hidden')}>
          <Card className="p-2">
            {sections.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-ink">{t('sectionsEmpty')}</p>
                <p className="mt-1 text-xs text-ink-muted">{t('sectionsEmptyHint')}</p>
              </div>
            ) : (
              <SortableList items={sections} className="flex flex-col gap-1" onReorder={commit}>
                {(section, index) => (
                  <SortableItem key={section.id} id={section.id}>
                    {(handle) => (
                      <SectionRow
                        section={section}
                        index={index}
                        selected={section.id === selectedId}
                        problems={problemsAt(index)}
                        handle={handle}
                        actions={actionsFor(index)}
                        onSelect={() => setSelectedId(section.id)}
                        onToggle={(enabled) => update(section.id, (current) => ({ ...current, enabled }))}
                      />
                    )}
                  </SortableItem>
                )}
              </SortableList>
            )}

            {removed ? (
              <div role="status" className="mx-1 mt-2 flex items-center justify-between gap-3 rounded-control border border-line bg-surface-sunken px-3 py-1.5 text-sm text-ink-muted">
                <span className="min-w-0 truncate">{t('sectionRemoved', { name: sectionName(removed.section.type, locale, catalog) })}</span>
                <Button variant="ghost" size="sm" onClick={undo}>
                  {t('undo')}
                </Button>
              </div>
            ) : null}

            <button
              type="button"
              disabled={full}
              onClick={() => setAdding({ after: null })}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-control border border-dashed border-line px-3 py-2.5 text-sm font-medium text-brand-700 transition-colors enabled:hover:border-brand-300 enabled:hover:bg-brand-50 disabled:cursor-not-allowed disabled:text-ink-faint"
            >
              <PlusIcon className="size-4" />
              {t('addSection')}
            </button>
            {full ? <p className="mt-1.5 px-1 text-center text-xs text-ink-muted">{t('sectionsFull', { max: count(MAX, locale) })}</p> : null}
          </Card>
        </div>

        {/* The chosen section. */}
        <div className={cx(!selected && 'max-xl:hidden')}>
          {selected ? (
            <SectionEditor
              key={selected.id}
              section={selected}
              problems={problemsAt(selectedIndex)}
              actions={actionsFor(selectedIndex)}
              onBack={() => setSelectedId(null)}
              onChange={(next) => update(selected.id, () => next)}
            />
          ) : (
            <Card className="flex min-h-80 flex-col items-center justify-center">
              <EmptyState title={t('sectionSelectPrompt')} body={t('sectionSelectPromptHint')} icon={<GridIcon />} />
            </Card>
          )}
        </div>
      </div>

      <SaveBar
        saving={saving === 'home.layout'}
        dirty={dirty}
        message={
          error ? (
            <Alert>{error}</Alert>
          ) : justSaved && !dirty ? (
            <span className="text-success">✓ {t('saved')}</span>
          ) : dirty && troubled > 0 ? (
            <span className="text-danger">{t('sectionsWithProblems', { count: count(troubled, locale) })}</span>
          ) : undefined
        }
        onSave={async () => {
          if (draft && (await save('home.layout', { ...layout, blocks: draft }))) {
            setDraft(null);
            setRemoved(null);
            setJustSaved(true);
          }
        }}
        onDiscard={
          dirty
            ? () => {
                setDraft(null);
                setRemoved(null);
                if (selectedId && !stored.some((section) => section.id === selectedId)) setSelectedId(null);
              }
            : undefined
        }
      />

      <AddSectionSheet
        open={adding !== null}
        after={adding?.after ?? null}
        sections={sections}
        catalog={catalog}
        themeName={theme?.theme?.name[locale] ?? null}
        flags={flags}
        onAdd={add}
        onClose={() => setAdding(null)}
      />
    </EditorContext.Provider>
  );
}

/**
 * A section already on the page may be copied whatever its flag says — the
 * flag decides whether it shows, not whether it may exist. Only the per-page
 * limit and the page's size stand in the way of a copy.
 */
function alwaysOn(type: string, catalog: HomeSectionCatalog): Record<string, boolean> {
  const flag = homeSectionDefinition(type, catalog)?.flag;
  return flag ? { [flag]: true } : {};
}

// ─── One row of the list ─────────────────────────────────────────────────────

function SectionRow({
  section,
  index,
  selected,
  problems,
  handle,
  actions,
  onSelect,
  onToggle,
}: {
  section: EditorSection;
  index: number;
  selected: boolean;
  problems: HomeLayoutProblem[];
  handle: { attributes: Record<string, unknown>; listeners: Record<string, unknown> };
  actions: Parameters<typeof Menu>[0]['items'];
  onSelect: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { catalog } = useContext(EditorContext);
  const Icon = typeIcon(section.type);
  const name = sectionName(section.type, locale, catalog);
  const definition = homeSectionDefinition(section.type, catalog);
  const fromTheme = definition === undefined || definition.group === 'theme';
  const summary = sectionSummary(section, locale, catalog);
  const items = sectionItemCount(section, catalog);
  const itemsCopy = definition?.items ? itemCopy(definition.items, locale) : null;
  const status = scheduleStatus(section, locale, t);

  return (
    <div
      className={cx(
        'group flex items-center gap-1 rounded-control border px-1 py-1 transition-colors',
        selected ? 'border-brand-300 bg-surface-sunken' : 'border-transparent hover:bg-surface-sunken',
      )}
    >
      <span
        {...handle.attributes}
        {...handle.listeners}
        aria-label={t('dragToReorder')}
        className="inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded-control text-ink-faint hover:bg-surface-sunken active:cursor-grabbing"
      >
        <GripIcon className="size-4" />
      </span>
      <button type="button" aria-current={selected || undefined} onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-2.5 py-0.5 text-start">
        <span
          aria-hidden
          className={cx(
            'inline-flex size-8 shrink-0 items-center justify-center rounded-chip transition-colors',
            selected ? 'bg-brand-50 text-brand-700' : 'bg-surface-sunken text-ink-muted',
            !section.enabled && 'opacity-50',
          )}
        >
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cx('flex items-center gap-1.5 text-sm font-medium', section.enabled ? 'text-ink' : 'text-ink-muted')}>
            <span className="truncate">{name}</span>
            <span className="sr-only">
              {' '}
              {count(index + 1, locale)}
            </span>
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-muted">
            {summary ? <span className="max-w-full truncate">{summary}</span> : null}
            {items !== null && itemsCopy && !summary && !(items === 0 && definition?.items?.placeholder) ? (
              <span data-numeric>{t('sectionItemsCount', { count: count(items, locale), item: itemsCopy.one })}</span>
            ) : null}
            {!section.enabled ? <Pill>{t('hidden')}</Pill> : null}
            {fromTheme ? <Pill tone="info">{t('sectionTheme')}</Pill> : null}
            {problems.length > 0 ? <Pill tone="danger">{t('sectionNeedsAttention')}</Pill> : null}
            {section.visibility === 'mobile' ? (
              <span className="inline-flex items-center gap-0.5" title={t('visibility_mobile')}>
                <PhoneIcon className="size-3.5" />
                <span className="sr-only">{t('visibility_mobile')}</span>
              </span>
            ) : section.visibility === 'desktop' ? (
              <span className="inline-flex items-center gap-0.5" title={t('visibility_desktop')}>
                <MonitorIcon className="size-3.5" />
                <span className="sr-only">{t('visibility_desktop')}</span>
              </span>
            ) : null}
            {status ? (
              <span className={cx('inline-flex items-center gap-0.5', status.ended && 'text-warning')}>
                <CalendarIcon className="size-3.5" />
                {status.text}
              </span>
            ) : null}
          </span>
        </span>
      </button>
      <Switch
        label={section.enabled ? `${t('sectionHide')} — ${name}` : `${t('sectionShow')} — ${name}`}
        checked={section.enabled}
        onChange={onToggle}
      />
      <Menu label={t('sectionActions')} items={actions} />
    </div>
  );
}

/** "Starts 1 Nov", "Until 30 Nov", "Ended" — or nothing for a section with no dates. */
function scheduleStatus(
  section: HomeLayoutBlock,
  locale: 'ar' | 'en',
  t: ReturnType<typeof useT>,
): { text: string; ended: boolean } | null {
  const startsAt = section.schedule?.startsAt ? Date.parse(section.schedule.startsAt) : NaN;
  const endsAt = section.schedule?.endsAt ? Date.parse(section.schedule.endsAt) : NaN;
  const now = Date.now();
  if (!Number.isNaN(endsAt) && now >= endsAt) return { text: t('scheduleEnded'), ended: true };
  if (!Number.isNaN(startsAt) && now < startsAt) return { text: t('scheduleUpcoming', { date: dateTime(section.schedule!.startsAt!, locale) }), ended: false };
  if (!Number.isNaN(endsAt)) return { text: t('scheduleRunning', { date: dateTime(section.schedule!.endsAt!, locale) }), ended: false };
  return null;
}

// ─── The chosen section ──────────────────────────────────────────────────────

function SectionEditor({
  section,
  problems,
  actions,
  onBack,
  onChange,
}: {
  section: EditorSection;
  problems: HomeLayoutProblem[];
  actions: Parameters<typeof Menu>[0]['items'];
  onBack: () => void;
  onChange: (next: EditorSection) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { catalog } = useContext(EditorContext);
  const definition = homeSectionDefinition(section.type, catalog);
  const Icon = typeIcon(section.type);
  const name = sectionName(section.type, locale, catalog);
  const config = section.config ?? {};
  const items = definition?.items;
  const top = useRef<HTMLDivElement>(null);

  useEffect(() => {
    top.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  // Problems the form cannot point at: the section's own, not a field's.
  const general = problems.filter((problem) => !problem.field);

  return (
    <Card className="overflow-hidden">
      <div ref={top} className="flex items-start gap-3 border-b border-line px-5 py-4">
        <IconButton label={t('sectionBack')} onClick={onBack} className="size-8 shrink-0 xl:hidden">
          <ArrowLeftIcon className="size-4 rtl:-scale-x-100" />
        </IconButton>
        <span aria-hidden className="inline-flex size-9 shrink-0 items-center justify-center rounded-chip bg-brand-50 text-brand-700">
          <Icon className="size-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink">{name}</h2>
          <p className="text-xs text-ink-muted">{sectionHint(section.type, locale, catalog) ?? t('sectionThemeNote')}</p>
        </div>
        <Switch
          label={section.enabled ? `${t('sectionHide')} — ${name}` : `${t('sectionShow')} — ${name}`}
          checked={section.enabled}
          onChange={(enabled) => onChange({ ...section, enabled })}
        />
        <Menu label={t('sectionActions')} items={actions} bordered />
      </div>

      <div className="flex flex-col gap-8 p-5">
        {general.length > 0 ? (
          <Alert>
            {general.map((problem, index) => (
              <span key={index} className="block">
                {problemMessage(problem, locale, catalog)}
              </span>
            ))}
          </Alert>
        ) : null}

        {definition ? (
          <Group title={t('sectionContent')}>
            <SectionFields
              type={section.type}
              fields={definition.fields}
              config={config}
              problems={problems.filter((problem) => problem.item === undefined && problem.field !== items?.name)}
              onChange={(next) => onChange({ ...section, config: next })}
            />
            {items ? (
              <>
                {problems
                  .filter((problem) => problem.item === undefined && problem.field === items.name)
                  .map((problem, index) => (
                    <p key={index} role="alert" className="text-xs text-danger">
                      {problemMessage(problem, locale, catalog)}
                    </p>
                  ))}
                <ItemsEditor
                  type={section.type}
                  items={items}
                  resetKey={section.id}
                  value={readSectionItems(config, items)}
                  problems={problems.filter((problem) => problem.item !== undefined)}
                  onChange={(next) => {
                    // Written in the new shape; an older plain list it was read from goes (ADR-057).
                    const nextConfig: Record<string, unknown> = { ...config, [items.name]: next };
                    if (items.legacy) delete nextConfig[items.legacy.from];
                    onChange({ ...section, config: nextConfig });
                  }}
                />
              </>
            ) : null}
          </Group>
        ) : (
          <p className="rounded-control border border-dashed border-line bg-surface-sunken/60 p-3 text-sm text-ink-muted">{t('sectionThemeNote')}</p>
        )}

        <DisplaySettings section={section} onChange={onChange} />
      </div>
    </Card>
  );
}

/** Where and when: every screen or only some, and the dates it runs between. */
function DisplaySettings({ section, onChange }: { section: EditorSection; onChange: (next: EditorSection) => void }) {
  const t = useT();
  const visibility: HomeSectionVisibility = section.visibility ?? 'all';

  const setSchedule = (part: 'startsAt' | 'endsAt', value: string) => {
    const schedule = { startsAt: section.schedule?.startsAt ?? null, endsAt: section.schedule?.endsAt ?? null, [part]: fromLocalInput(value) };
    const next = { ...section };
    if (!schedule.startsAt && !schedule.endsAt) delete next.schedule;
    else next.schedule = schedule;
    onChange(next);
  };

  return (
    <Group title={t('sectionDisplay')} hint={t('sectionDisplayHint')}>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('sectionVisibility')}</span>
        <div>
          <Segmented<HomeSectionVisibility>
            label={t('sectionVisibility')}
            value={visibility}
            items={(['all', 'mobile', 'desktop'] as const).map((id) => ({ id, label: t(`visibility_${id}`) }))}
            onChange={(next) => {
              const copy = { ...section };
              if (next === 'all') delete copy.visibility;
              else copy.visibility = next;
              onChange(copy);
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('sectionSchedule')}</span>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-muted">{t('scheduleStarts')}</span>
            <Input type="datetime-local" dir="ltr" value={toLocalInput(section.schedule?.startsAt)} onChange={(event) => setSchedule('startsAt', event.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-muted">{t('scheduleEnds')}</span>
            <Input type="datetime-local" dir="ltr" value={toLocalInput(section.schedule?.endsAt)} onChange={(event) => setSchedule('endsAt', event.target.value)} />
          </label>
        </div>
        <p className="text-xs text-ink-muted">{t('sectionScheduleHint')}</p>
      </div>
    </Group>
  );
}

// ─── Adding a section ────────────────────────────────────────────────────────

function AddSectionSheet({
  open,
  after,
  sections,
  catalog,
  themeName,
  flags,
  onAdd,
  onClose,
}: {
  open: boolean;
  after: number | null;
  sections: EditorSection[];
  catalog: HomeSectionCatalog;
  /** The theme's own name, for its group; null when it gave none. */
  themeName: string | null;
  flags: Record<string, boolean>;
  onAdd: (type: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const anchor = after !== null ? sections[after] : undefined;

  return (
    <Sheet
      open={open}
      size="lg"
      title={t('addSectionTitle')}
      subtitle={anchor ? t('addSectionAfter', { name: sectionSummary(anchor, locale, catalog) ?? sectionName(anchor.type, locale, catalog) }) : t('addSectionAtEnd')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-6">
        {GROUP_ORDER.filter((group) => [...catalog.values()].some((definition) => definition.group === group)).map((group) => (
          <section key={group}>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-muted uppercase">
              {group === 'theme' && themeName ? themeName : GROUP_COPY[group][locale]}
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {[...catalog.values()].filter((definition) => definition.group === group).map((definition) => {
                const Icon = typeIcon(definition.type);
                const blocker = addBlocker(definition.type, sections, flags, MAX, catalog);
                const reason =
                  blocker?.reason === 'perPage'
                    ? blocker.limit === 1
                      ? t('sectionOnePerPage')
                      : t('sectionAtMost', { count: count(blocker.limit ?? 0, locale) })
                    : blocker?.reason === 'flag'
                      ? t('sectionNeedsFlag', { flag: FLAG_COPY[blocker.flag ?? '']?.[locale] ?? blocker.flag ?? '' })
                      : blocker?.reason === 'full'
                        ? t('sectionsFull', { max: count(MAX, locale) })
                        : null;
                return (
                  <button
                    key={definition.type}
                    type="button"
                    disabled={blocker !== null}
                    onClick={() => onAdd(definition.type)}
                    className="group flex items-start gap-3 rounded-control border border-line bg-surface p-3 text-start transition-colors enabled:hover:border-brand-300 enabled:hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    <span
                      aria-hidden
                      className="inline-flex size-9 shrink-0 items-center justify-center rounded-chip bg-surface-sunken text-ink-muted transition-colors group-enabled:group-hover:bg-brand-50 group-enabled:group-hover:text-brand-700"
                    >
                      <Icon className="size-[18px]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-ink">{sectionName(definition.type, locale, catalog)}</span>
                      <span className="block text-xs text-ink-muted">{reason ?? sectionHint(definition.type, locale, catalog)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Sheet>
  );
}
